import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Download, Mail, ExternalLink, Layers, Zap } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { api } from "@/lib/api";
import { usePageContent } from "@/lib/content";
import { CTABlock } from "@/components/common/CTABlock";
import { absUrl } from "@/components/admin/UploadWidget";

export function SuccessStories() {
    useSEO({
        title: "Success Stories | RKS Systems",
        description: "Real customer outcomes across BFSI, retail, logistics, manufacturing and more.",
    });
    const pc = usePageContent("success-stories");
    const [items, setItems] = useState([]);
    const [industry, setIndustry] = useState("all");
    useEffect(() => {
        api.get("/success-stories", { params: industry === "all" ? {} : { industry } }).then(({ data }) => setItems(data.items || []));
    }, [industry]);
    const industries = ["all", ...Array.from(new Set(items.map((i) => i.industry)))];
    return (
        <div data-testid="page-stories">
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-24 lg:py-32">
                    <div className="max-w-3xl">
                        <span className="overline text-primary">Success stories</span>
                        <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-extrabold tracking-tight leading-[1.05]">
                            {pc?.hero_headline || "Proven Enterprise Engineering & AI Transformation Outcomes"}
                        </h1>
                        <p className="mt-6 text-lg text-white/70 leading-relaxed">
                            {pc?.hero_subheadline || "Explore real-world case studies and production deployments across Retail, BFSI, Logistics, and Manufacturing. From AI-driven inventory intelligence to enterprise AWS cloud modernization, discover how RKS Systems solves complex operational challenges with measurable ROI."}
                        </p>
                    </div>
                </div>
            </section>
            <section className="py-16 lg:py-20">
                <div className="container-app">
                    <div className="flex flex-wrap gap-2 mb-10">
                        {industries.map((c) => (
                            <button key={c} onClick={() => setIndustry(c)} data-testid={`stories-filter-${c}`}
                                className={`text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-full border transition-all ${
                                    industry === c ? "bg-primary text-primary-foreground border-primary" : "bg-white text-foreground/70 border-border hover:border-primary/40"
                                }`}>
                                {c === "all" ? "All" : c}
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {items.map((s) => (
                            <Link key={s.id} to={`/success-stories/${s.id}`} data-testid={`story-card-${s.id}`}
                                className="group overflow-hidden rounded-xl border border-border bg-card transition-all hover:shadow-lg hover:-translate-y-1">
                                <div className="aspect-[16/9] overflow-hidden bg-secondary">
                                    <img src={absUrl(s.cover_image)} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                </div>
                                <div className="p-7">
                                    <span className="overline">{s.industry}</span>
                                    <h3 className="mt-3 font-heading text-xl font-bold leading-snug">{s.title}</h3>
                                    <p className="mt-3 text-sm text-muted-foreground line-clamp-2">{s.challenge}</p>
                                    <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                                        Read case study <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
            <CTABlock />
        </div>
    );
}

// ── Section heading helper ───────────────────────────────────────────────────
function SectionH2({ children }) {
    return <h2 className="font-heading text-2xl font-bold">{children}</h2>;
}

export function SuccessStoryDetail() {
    const { id } = useParams();
    const [item, setItem] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [pdfEmailCapture, setPdfEmailCapture] = useState("");
    const [pdfGateOpen, setPdfGateOpen] = useState(false);
    useSEO({ title: item ? `${item.title} | RKS Systems` : "Success story | RKS Systems", description: item?.sub_headline || item?.challenge });
    useEffect(() => {
        api.get(`/success-stories/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
    }, [id]);
    if (notFound) return <Navigate to="/success-stories" replace />;
    if (!item) return <div className="container-app py-24 text-center text-muted-foreground">Loading...</div>;

    const hasImpactMetrics = Array.isArray(item.impact_metrics) && item.impact_metrics.length > 0;
    const hasPainPoints    = Array.isArray(item.pain_points)    && item.pain_points.length > 0;
    const hasArchLayers    = Array.isArray(item.architecture_layers) && item.architecture_layers.length > 0;
    const hasRoiMatrix     = Array.isArray(item.roi_matrix)     && item.roi_matrix.length > 0;
    const hasTechCategories = Array.isArray(item.tech_stack_categories) && item.tech_stack_categories.length > 0;
    const flatStack         = !hasTechCategories && Array.isArray(item.tech_stack) && item.tech_stack.length > 0;

    const handlePdfDownload = () => {
        if (!item.pdf_asset_url) return;
        if (item.pdf_gate_email) { setPdfGateOpen(true); return; }
        window.open(item.pdf_asset_url, "_blank");
    };
    const submitPdfGate = (e) => {
        e.preventDefault();
        if (!pdfEmailCapture) return;
        window.open(item.pdf_asset_url, "_blank");
        setPdfGateOpen(false);
    };

    return (
        <div data-testid={`page-story-${item.id}`}>

            {/* ── HERO ── */}
            <section className="bg-[hsl(222_47%_7%)] text-white noise">
                <div className="container-app py-16 lg:py-24">
                    <Link to="/success-stories" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
                        <ArrowLeft className="h-3.5 w-3.5" /> All success stories
                    </Link>
                    <span className="overline text-primary">{item.industry}</span>
                    <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] max-w-4xl">{item.title}</h1>
                    {item.sub_headline && (
                        <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-3xl">{item.sub_headline}</p>
                    )}
                </div>
            </section>

            {/* ── EXECUTIVE IMPACT BAR ── */}
            {hasImpactMetrics && (
                <section className="bg-primary" data-testid="story-impact-bar">
                    <div className="container-app">
                        <div className={`grid grid-cols-2 lg:grid-cols-${Math.min(item.impact_metrics.length, 4)} divide-x divide-white/20`}>
                            {item.impact_metrics.slice(0, 4).map((m, i) => (
                                <div key={i} className="px-6 py-6 lg:py-7 text-white">
                                    <div className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight">{m.value}</div>
                                    <div className="mt-1 text-sm font-semibold text-white/90">{m.label}</div>
                                    {m.sub_text && <div className="mt-1 text-xs text-white/60 leading-snug">{m.sub_text}</div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ── MAIN CONTENT ── */}
            <section className="py-16 lg:py-20">
                <div className="container-app max-w-4xl space-y-14">

                    {/* Cover image */}
                    {item.cover_image && (
                        <div className="aspect-[16/8] overflow-hidden rounded-xl border border-border bg-secondary">
                            <img src={absUrl(item.cover_image)} alt={item.title} className="h-full w-full object-cover" />
                        </div>
                    )}

                    {/* ── MODULE 2a: Challenge ── */}
                    {(item.challenge || hasPainPoints) && (
                        <div>
                            <SectionH2>Challenge</SectionH2>
                            {item.challenge && <p className="mt-3 text-foreground/80 leading-relaxed">{item.challenge}</p>}

                            {/* Pain Points Matrix Table */}
                            {hasPainPoints && (
                                <div className="mt-6 overflow-x-auto" data-testid="story-pain-points">
                                    <table className="w-full text-sm border border-border rounded-xl overflow-hidden">
                                        <thead className="bg-secondary/60">
                                            <tr>
                                                {["Challenge", "Root Cause", "Business Impact"].map(h => (
                                                    <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {item.pain_points.map((row, i) => (
                                                <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                                                    <td className="px-4 py-3 font-medium">{row.challenge}</td>
                                                    <td className="px-4 py-3 text-muted-foreground">{row.root_cause}</td>
                                                    <td className="px-4 py-3 text-muted-foreground">{row.business_impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── MODULE 2b: Solution ── */}
                    {item.solution && (
                        <div>
                            <SectionH2>Solution</SectionH2>
                            <p className="mt-3 text-foreground/80 leading-relaxed">{item.solution}</p>
                        </div>
                    )}

                    {/* ── MODULE 2c: Architecture ── */}
                    {(item.architecture_diagram || hasArchLayers || item.technical_capabilities) && (
                        <div data-testid="story-architecture">
                            <SectionH2>Architecture &amp; Technical Design</SectionH2>

                            {item.architecture_diagram && (
                                <div className="mt-5 rounded-xl border border-border overflow-hidden bg-secondary/30">
                                    <img src={absUrl(item.architecture_diagram)} alt="Architecture diagram" className="w-full object-contain max-h-[560px]" />
                                </div>
                            )}

                            {hasArchLayers && (
                                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {item.architecture_layers.map((layer, i) => (
                                        <div key={i} className="rounded-lg border border-border bg-white p-4 flex gap-3">
                                            <div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex-shrink-0 flex items-center justify-center font-bold text-xs">
                                                {layer.step || (i + 1)}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm">{layer.name}</div>
                                                {layer.description && <div className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{layer.description}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {item.technical_capabilities && (
                                <div className="mt-6 rounded-xl border border-border bg-secondary/30 p-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Layers className="h-4 w-4 text-primary" strokeWidth={1.75} />
                                        <span className="text-xs font-bold uppercase tracking-widest text-primary">Deep-Dive Technical Capabilities</span>
                                    </div>
                                    <ul className="space-y-1.5">
                                        {item.technical_capabilities.split("\n").filter(Boolean).map((cap, i) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-foreground/80">
                                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                                                {cap}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── MODULE 3a: Results ── */}
                    {Array.isArray(item.results) && item.results.length > 0 && (
                        <div>
                            <SectionH2>Results</SectionH2>
                            <ul className="mt-3 space-y-2.5">
                                {item.results.map((r) => (
                                    <li key={r} className="flex items-start gap-3 text-foreground/85">
                                        <CheckCircle2 className="h-4 w-4 mt-1 text-primary flex-shrink-0" strokeWidth={1.75} />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* ── MODULE 3b: ROI Matrix ── */}
                    {hasRoiMatrix && (
                        <div data-testid="story-roi-matrix">
                            <SectionH2>Implementation Quality &amp; ROI</SectionH2>
                            <div className="mt-5 overflow-x-auto">
                                <table className="w-full text-sm border border-border rounded-xl overflow-hidden">
                                    <thead className="bg-secondary/60">
                                        <tr>
                                            {["Performance Category", "Quality Implementation Standard", "Verified Business Outcome"].map(h => (
                                                <th key={h} className="px-4 py-3 text-left text-xs font-bold uppercase tracking-widest text-muted-foreground border-b border-border">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {item.roi_matrix.map((row, i) => (
                                            <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/30 transition-colors">
                                                <td className="px-4 py-3 font-semibold text-primary">{row.category}</td>
                                                <td className="px-4 py-3 text-muted-foreground text-xs leading-relaxed">{row.quality_standard}</td>
                                                <td className="px-4 py-3 text-foreground/80 text-xs leading-relaxed">{row.verified_outcome}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* ── MODULE 3c: Technology Stack ── */}
                    {(hasTechCategories || flatStack) && (
                        <div>
                            <SectionH2>Technology Stack</SectionH2>
                            {hasTechCategories ? (
                                <div className="mt-4 space-y-4" data-testid="story-tech-categories">
                                    {item.tech_stack_categories.map((cat, i) => (
                                        <div key={i}>
                                            <div className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">{cat.category}</div>
                                            <div className="flex flex-wrap gap-2">
                                                {(Array.isArray(cat.technologies) ? cat.technologies : []).map((t) => (
                                                    <span key={t} className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-3.5 py-1.5 text-xs font-semibold text-secondary-foreground">{t}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-3 flex flex-wrap gap-2" data-testid="story-tech-stack">
                                    {item.tech_stack.map((t) => (
                                        <span key={t} className="inline-flex items-center rounded-full border border-border bg-secondary/60 px-3.5 py-1.5 text-xs font-semibold text-secondary-foreground">{t}</span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── MODULE 4: CTA + PDF + Cross-sell ── */}
                    {(item.cta_headline || item.pdf_asset_url || item.cross_sell_product) && (
                        <div className="rounded-2xl bg-[hsl(222_47%_7%)] text-white p-8 lg:p-10" data-testid="story-cta-block">
                            {item.cta_headline && (
                                <h2 className="font-heading text-2xl lg:text-3xl font-bold">{item.cta_headline}</h2>
                            )}
                            {item.cta_sub_copy && (
                                <p className="mt-3 text-white/70 leading-relaxed">{item.cta_sub_copy}</p>
                            )}

                            <div className="mt-7 flex flex-wrap gap-3">
                                {/* Primary CTA */}
                                <Link
                                    to={item.cta_primary_url || "/schedule-meeting"}
                                    className="inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 font-semibold text-white hover:bg-primary/90 transition-colors text-sm"
                                    data-testid="story-cta-primary">
                                    Book a 30-Min Session <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                                </Link>

                                {/* PDF Download */}
                                {item.pdf_asset_url && (
                                    <button onClick={handlePdfDownload}
                                        className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/20 transition-colors text-sm"
                                        data-testid="story-cta-pdf">
                                        <Download className="h-4 w-4" strokeWidth={1.75} />
                                        Download Architecture &amp; ROI Case Study
                                    </button>
                                )}
                            </div>

                            {/* Cross-sell product callout */}
                            {item.cross_sell_product && (
                                <div className="mt-6 rounded-xl border border-white/15 bg-white/5 px-5 py-4 flex items-center gap-3">
                                    <Zap className="h-5 w-5 text-primary flex-shrink-0" strokeWidth={1.75} />
                                    <div>
                                        <span className="text-xs font-bold uppercase tracking-widest text-primary/80">Also explore</span>
                                        <div className="mt-0.5 font-semibold text-sm">{item.cross_sell_product}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </section>

            {/* PDF Email Gate Modal */}
            {pdfGateOpen && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
                    <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-7" data-testid="pdf-gate-modal">
                        <Mail className="h-8 w-8 text-primary mb-4" strokeWidth={1.75} />
                        <h3 className="font-heading text-xl font-bold">Get the Full Case Study PDF</h3>
                        <p className="mt-2 text-sm text-muted-foreground">Enter your work email to download the Architecture & ROI PDF instantly.</p>
                        <form onSubmit={submitPdfGate} className="mt-5 flex gap-2">
                            <input required type="email" value={pdfEmailCapture} onChange={e => setPdfEmailCapture(e.target.value)}
                                placeholder="you@company.com" data-testid="pdf-gate-email"
                                className="flex-1 rounded-md border border-input px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
                            <button type="submit" className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90" data-testid="pdf-gate-submit">
                                Download
                            </button>
                        </form>
                        <button onClick={() => setPdfGateOpen(false)} className="mt-3 text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                </div>
            )}

            <CTABlock />
        </div>
    );
}


